<template>
    <section>

      <el-col :span="24">

        <!-- XOL-596 (readable remarks): the headings are real h5.remarks-heading above BOTH states
             (editor or read-only block); the read-only block is the shared ReadOnlyRemarks. -->
        <section class="remarks-section">

          <h5 class="remarks-heading">General Remarks</h5>
          <!-- XOL-526 (S5, D9): the editor is now the honest write gate
               (canWriteOwnRequest() — Tower Co's own canWriteStep(), or
               isCounterpartyWriter() for a non-Tower-Co viewer) instead of
               the old `isTelco()`, which rendered a "live" editor for every
               Telco even though canWriteStep() (Tower-Co-only) silently
               discarded the save (D5). A Telco on a non-RTB request now
               correctly lands in the read-only branch below. -->
          <!-- XOL-596 (completed steps; Carlos, 2026-10-14: "General remarks are per step"):
               General Remarks is step 1's here, so it is read-only once step 1 is complete
               (it falls into the read-only block below). Site -> Actions (ActionDetails)
               still edits it: that form sends no step context. -->
          <div v-if="canWriteOwnRequest() && !isTowerCo() && isActive(actionData) && !readOnly && !frozen">
            <small v-if="generalRemarksNote" class="text-muted">{{generalRemarksNote}}</small>
            <html-input v-model="actionData.remarks" clsStyle="width: 52.8vw; overflow: auto;" :disabled="!canWriteOwnRequest() || readOnly"
              @focus="holdField(actionData, 'remarks')" @blur="releaseField(actionData, 'remarks')" @input="fieldChanged(actionData, 'remarks')" />
            <field-cue :row="actionData" path="remarks" label="General Remarks" layout="block" />
            <small v-if="generalRemarksStamp" class="remarks-stamp">{{lastUpdatedLine(generalRemarksStamp)}}</small>
          </div>
          <!-- XOL-526 (UX U-H, step1.generalRemarks.empty): was "No client
               remarks" — wrong for a non-Telco viewer of this read-only
               branch. Since SPA-2/XOL-534, a backhaul provider on an active
               RTB gets the editor above instead; this branch is for
               everyone the canWriteOwnRequest()/isCounterpartyWriter() gate
               still excludes. -->
          <!-- The `<hr v-if="step.remarks">` that used to close this section is gone: its
               condition was the OTHER field's, and the section margin now separates the two. -->
          <read-only-remarks v-else aria-label="General Remarks" :html="actionData.remarks"
            :empty-text="copyText('requestSync.remarks.emptyGeneral')"
            :stamp="generalRemarksStamp ? lastUpdatedLine(generalRemarksStamp) : null" />

        </section>


        <!-- The Progress Remarks heading was a closable info el-alert, shown only while the request
             was active: closing it (or a Closed / Rejected request) left the text unlabelled. -->
        <!-- Carlos, 2026-10-15: a counterparty's Progress Remarks is its party-2 row's field, which is
             written only on an RTB; on another request type it is not part of the form. -->
        <section v-if="stepRemarksInForm(step)" class="remarks-section">
          <h5 class="remarks-heading">Progress Remarks</h5>

          <!-- XOL-526 (S5, D9): was `!canWriteStep()` — a counterparty may now
               write its own party-2 row (canWriteOwnStep()), which also folds
               in the party-1-step-completed lock (D9/A10). Tower Co unchanged. -->
          <!-- XOL-596 (completed steps, CS-R): a completed step's remarks are a read-only
               block for every viewer, with an explicit empty state (a disabled, empty
               HtmlInput renders nothing at all). The concurrent-editing note goes with the
               editor. -->
          <!-- XOL-608: also for a viewer who cannot write it (a view-only Tower Co user, a
               counterparty that is not the owner or provider, a request that is no longer
               active): the same block, never a disabled HtmlInput (empty, it rendered nothing
               at all; with text, a fixed 111px box). The editor renders only when it would be
               enabled. -->
          <read-only-remarks v-if="progressRemarksReadOnly" aria-label="Progress Remarks" :html="step.remarks"
            :stamp="progressRemarksStamp ? lastUpdatedLine(progressRemarksStamp) : null" />
          <template v-else>
            <small v-if="progressRemarksNote" class="text-muted">{{progressRemarksNote}}</small>
            <html-input v-model="step.remarks" :disabled="!canWriteOwnStep(step) || !isActive(actionData)"
              @focus="holdField(step, 'remarks')" @blur="releaseField(step, 'remarks')" @input="fieldChanged(step, 'remarks')" />
            <field-cue :row="step" path="remarks" label="Progress Remarks" layout="block" />
            <small v-if="progressRemarksStamp" class="remarks-stamp">{{lastUpdatedLine(progressRemarksStamp)}}</small>
          </template>
        </section>

    </el-col>

  </el-col>

  </section>
</template>



<script>

import SiteMixins from '@/mixins/SiteMixins'

import RequestMixins from '@/mixins/RequestMixins'

import FileDownload from '@/components/atoms/files/FileDownload'
import FileUpload from '@/components/atoms/files/FileUpload'
import FieldCue from '@/components/requests/FieldCue'
import ReadOnlyRemarks from '@/components/atoms/ReadOnlyRemarks'

import { copyText } from '@/lib/requestSyncCopy'

export default {

  // XOL-534 (reviewer): DateMixins (formatDateTime, used by lastUpdatedLine)
  // already arrives transitively via RequestMixins -> SiteMixins -> DateMixins
  // — a direct import here was redundant.
  mixins: [SiteMixins, RequestMixins],

  components: {
    FieldCue,
    FileDownload,
    FileUpload,
    ReadOnlyRemarks
  },


  props: {

    rowData: {
      type: Object,
      required: true
    },

    rowIndex: {
      type: Number,
      default: null
    },

    stepData: {
      type: Object,
      default: null
    },

    readOnly: {
      type: Boolean,
      default: true
    }

  },


  methods: {

    copyText (key, params) {
      return copyText(key, params)
    },

    // XOL-596: the fresh GET on open is adopted IN PLACE (F4: one object per
    // row), never swapped in, with the seq of when it was issued; a field the user
    // is already editing keeps its text (R2.7).
    loadOwnStep () {
      this.loadStep(this.stepData.id, this.step)
    }

  },


  computed: {

    disabled () {
      return !this.canWriteStep() || this.isViewOnly() || this.readOnly
    },

    // XOL-596 (completed steps): step 1 is complete, so everything in it is read-only for
    // every viewer until it is re-opened. A CONSTANT step number (never `step.stepNo`:
    // RequestWorkflow can transiently repoint `step`), read from the server baseline.
    frozen () {
      return this.isStepFrozen(1)
    },

    // XOL-526 (UX §2.4, U-I): General Remarks is a single shared
    // `actions.remarks` column — Tower Co, the telco owner and the provider
    // all read the very same value, so its D12 stamp is shown to every
    // viewer, in either branch (unlike a per-party step row).
    generalRemarksStamp () {
      return this.editStamp(this.actionData.state, 'remarks')
    },

    // UX item 17: names the Tower Co as the other editor, with or without a
    // distinct provider.
    generalRemarksNote () {
      return (this.isCounterpartyWriter() && this.isActive(this.actionData) && !this.readOnly && !this.frozen)
        ? this.sharedGeneralRemarksNote()
        : null
    },

    // Progress Remarks is the counterparty's own party-2 STEP row — Tower
    // Co's own row (party 1) is a different object entirely and is never
    // shared, so it gets no note/stamp.
    progressRemarksStamp () {
      return (!this.isTowerCo() && this.step) ? this.editStamp(this.step.state, 'remarks') : null
    },

    progressRemarksNote () {
      return (this.canWriteOwnStep(this.step) && !this.isTowerCo() && this.isActive(this.actionData) && this.hasDistinctProvider())
        ? this.sharedFieldNote('edit these remarks')
        : null
    },

    // XOL-608: the read-only block shows exactly when the editor's own `:disabled` would be
    // true, OR'd with the step being complete (XOL-596). Reactive, so a live flip (the
    // request closes, the viewer stops being a writer) swaps the editor for the block.
    progressRemarksReadOnly () {
      return this.frozen || !this.canWriteOwnStep(this.step) || !this.isActive(this.actionData)
    }

  },


  watch: {

    // The editors unmount when the step freezes, and an unmounting quill gets no blur:
    // let go of the text paths they hold, so nothing stays held by focus.
    frozen (now) {
      if (now) {
        this.releaseField(this.actionData, 'remarks')
        this.releaseField(this.step, 'remarks')
      }
    },

    // XOL-608: the Progress Remarks editor also unmounts when the viewer can no longer write
    // it (the request goes inactive, a counterparty stops being a writer), and an unmounting
    // quill gets no blur: let go of the path it holds. (Today's disabled path leaked the same.)
    progressRemarksReadOnly (now) {
      if (now) {
        this.releaseField(this.step, 'remarks')
      }
    }

  },


  data () {

    return {
      actionData: this.rowData,
      step: this.stepData
    }

  },  // data


  created () {

    // XOL-526 (S5, D9): was `canWriteStep()` — a counterparty may now write its
    // own party-2 row too. XOL-596: the GET is adopted in place and nothing is
    // baselined here: a row's baseline is its registered shadow.
    if ((this.canWriteStep() || this.canWriteOwnStep(this.step)) && !this.readOnly && this.step) {
      this.loadOwnStep()
    }

  },


  beforeDestroy () {

    // XOL-596: no save here. The fields of this step save at the form's pause (or
    // at a flush point); a text field still focused gets no blur on unmount.
    this.releaseFocused()
    this.$emit('updated', this.step)

  }

}

</script>
